import { useEffect, useState } from "react";
import { isIOS, isStandalone } from "../utils/push";

// Shown after a visitor's 3rd session, same threshold Diver's Horizon uses for its own install nudge —
// dismissible and remembered via localStorage, never shown again once dismissed or once already installed
// (isStandalone()). Two paths: real browsers that fire `beforeinstallprompt` (Android/desktop Chrome/Edge)
// get an actual Install button; iOS Safari never fires that event, so it gets manual instructions instead
// — this is also the natural place to explain that installing is what unlocks powder alerts on iPhone
// (see needsHomeScreenInstall() in src/utils/push.js).
const SESSION_KEY = "ph_session_count";
const DISMISSED_KEY = "ph_install_dismissed";
const MIN_SESSIONS = 3;

function readLocal(key) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeLocal(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Private browsing / storage blocked — the prompt just won't remember dismissal or session count.
  }
}

export function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [dismissed, setDismissed] = useState(true);
  const [eligible, setEligible] = useState(false);
  const [ios, setIos] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    setIos(isIOS());
    setDismissed(readLocal(DISMISSED_KEY) === "1");

    const sessionCount = Number(readLocal(SESSION_KEY) || "0") + 1;
    writeLocal(SESSION_KEY, String(sessionCount));
    setEligible(sessionCount >= MIN_SESSIONS);

    function handleBeforeInstallPrompt(e) {
      e.preventDefault();
      setDeferredPrompt(e);
    }
    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    return () => window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
  }, []);

  function dismiss() {
    writeLocal(DISMISSED_KEY, "1");
    setDismissed(true);
  }

  async function install() {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try {
      await deferredPrompt.userChoice;
    } catch {
      // no-op — userChoice can reject if the prompt is dismissed unusually
    }
    setDeferredPrompt(null);
    dismiss();
  }

  const canInstall = ios || !!deferredPrompt;
  if (dismissed || !eligible || !canInstall) return null;

  return (
    <div className="install-prompt glass">
      <button className="auth-close-btn" onClick={dismiss} aria-label="Dismiss">
        ×
      </button>
      <div className="resort-name">Install Pow Horizon</div>
      <p className="sec-dek">
        {ios && !deferredPrompt
          ? "Tap Share → Add to Home Screen for the full app experience — this also unlocks powder alerts on iPhone."
          : "Install for a full-screen app experience and faster loading."}
      </p>
      {deferredPrompt && (
        <button className="btn-accent" onClick={install}>
          Install
        </button>
      )}
    </div>
  );
}
